import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../app/store.ts';
import { funde as eingang, KeinEinlieferer, unvollstaendig, type Fund } from '../funde/store.ts';
import { deriveTitle } from '../data/markdown.ts';
import { matches, parseQuery } from '../lib/search.ts';
import { Icon } from './Icon.tsx';

/**
 * Gesammeltes (docs/KONZEPT.md, Abschnitt 20).
 *
 * Was der Einlieferer hereinreicht, ist weder Notiz noch Beleg: ein Video, eine
 * Webseite, eine hingesprochene Idee. Deshalb ein eigener Bereich mit derselben
 * Regalansicht wie die Dokumente — das Deckblatt trägt hier die Wiedererkennung,
 * denn an drei Zeilen Transkript erkennt man ein TikTok nicht wieder.
 */

const HERKUNFT: Record<string, string> = {
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  website: 'Webseite',
  sprachnachricht: 'Sprachnachricht',
  datei: 'Datei',
};

type Props = { onClose: () => void };

export function Funde({ onClose }: Props): JSX.Element {
  const { funde, refresh, open } = useApp();
  const [filter, setFilter] = useState('');
  const [lauf, setLauf] = useState<{ fertig: number; gesamt: number } | null>(null);
  const [bericht, setBericht] = useState<string | null>(null);
  const [bereit, setBereit] = useState<boolean | null>(null);
  const [offen, setOffen] = useState(0);

  const lebend = funde.filter((eintrag) => eintrag.deletedAt === null);
  const query = useMemo(() => parseQuery(filter), [filter]);
  const shown = lebend.filter((eintrag) => matches(eintrag, query));
  const luecken = lebend.filter((eintrag) => unvollstaendig(eintrag) && eintrag.fund.url);

  useEffect(() => {
    void eingang.lage().then((lage) => setBereit(lage.bereit));
  }, []);

  /**
   * Abholen ist der einzige Weg, auf dem hier etwas erscheint — und er läuft auch
   * beim Öffnen des Bereichs, damit niemand einen Knopf suchen muss, um zu sehen,
   * was längst da ist.
   */
  async function abholen(stumm: boolean): Promise<void> {
    if (bereit === false) return;
    setLauf({ fertig: 0, gesamt: 0 });
    try {
      const ergebnis = await eingang.abholen((fertig, gesamt) => setLauf({ fertig, gesamt }));
      await refresh();
      await standHolen();
      if (ergebnis.neu > 0 || ergebnis.unlesbar > 0) {
        const teile = [`${ergebnis.neu} übernommen`];
        if (ergebnis.unlesbar > 0) {
          teile.push(`${ergebnis.unlesbar} nicht zu öffnen — vermutlich ein anderer Schlüssel`);
        }
        setBericht(teile.join(', '));
      } else if (!stumm) {
        setBericht('Nichts Neues im Eingang.');
      }
    } catch {
      if (!stumm) setBericht('Der Eingang war nicht erreichbar.');
    } finally {
      setLauf(null);
    }
  }

  useEffect(() => {
    if (bereit === true) void abholen(true);
    // Nur beim Öffnen und wenn die Einrichtung feststeht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bereit]);

  /**
   * Wie viel beim Einlieferer liegt. Nur beim Öffnen und nach den beiden Aktionen,
   * die daran etwas ändern — `funde` als Abhängigkeit hätte die Abfrage an jede
   * Notizänderung gehängt, und das sind beim Tippen viele.
   */
  async function standHolen(): Promise<void> {
    const stand = await eingang.auftraege().catch(() => ({ offen: 0, laufend: 0 }));
    setOffen(stand.offen + stand.laufend);
  }

  useEffect(() => {
    void standHolen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Nachholen: Der Einlieferer bekommt den Auftrag, das Ergebnis ersetzt später den
   * Torso. Es passiert also nichts sofort Sichtbares — deshalb sagt die Meldung, dass
   * es unterwegs ist, statt so zu tun, als wäre es schon da.
   */
  async function nachholen(stuecke: Fund[]): Promise<void> {
    if (stuecke.length === 0) return;
    setBericht(null);
    try {
      const ergebnis = await eingang.beauftragen(stuecke);
      await standHolen();
      const teile: string[] = [];
      if (ergebnis.beauftragt > 0) {
        teile.push(
          `${ergebnis.beauftragt} beim Einlieferer angefragt — das dauert ein paar Minuten, danach „Eingang jetzt holen".`,
        );
      }
      if (ergebnis.ohneAdresse > 0) {
        teile.push(`${ergebnis.ohneAdresse} ohne Adresse, die lassen sich nicht nachholen.`);
      }
      setBericht(teile.join(' '));
    } catch (fehler) {
      setBericht(
        fehler instanceof KeinEinlieferer
          ? 'Kein Einlieferer meldet sich. Läuft der Dienst, und ist er neu genug, um Aufträge anzunehmen?'
          : 'Der Auftrag ging nicht raus.',
      );
    }
  }

  function kachel(eintrag: Fund): JSX.Element {
    const meta = eintrag.fund;
    const titel = deriveTitle(eintrag.content);
    const fehlt = unvollstaendig(eintrag);
    return (
      <li className="doc-tile" key={eintrag.id}>
        <button className="doc-cover" onClick={() => open(eintrag.id)} title={titel}>
          {meta.vorschau ? (
            <img src={meta.vorschau} alt="" />
          ) : (
            <span className="doc-cover-text">{eintrag.content.replace(/\s+/g, ' ').slice(0, 220)}</span>
          )}
          <span className="doc-flag">{HERKUNFT[meta.quelle] ?? meta.quelle}</span>
          {fehlt && <span className="doc-flag doc-flag-warn">unvollständig</span>}
        </button>
        <button className="doc-tile-title" onClick={() => open(eintrag.id)}>
          {titel}
        </button>
        <span className="doc-tile-sub">
          {/* Das Datum des Eingangs, nicht das des Videos: Die Kachel muss zeigen,
              wonach sortiert wird, sonst wirkt die Regalansicht durcheinander. */}
          {new Date(meta.eingang).toLocaleDateString('de-DE')}
          {meta.autor ? ` · ${meta.autor}` : ''}
          {meta.url && (
            <>
              {' · '}
              <button className="doc-tile-link" onClick={() => void nachholen([eintrag])}>
                nochmal holen
              </button>
            </>
          )}
        </span>
      </li>
    );
  }

  return (
    <section className="archive">
      <header className="archive-head">
        <span className="archive-title">Gesammeltes</span>
        <input
          className="archive-search"
          placeholder="Im Gesammelten suchen"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <button className="head-icon" onClick={onClose} title="Schließen">
          <Icon name="close" />
        </button>
      </header>

      <div className="archive-body">
        {bereit === false && (
          <p className="todos-hint">
            Der versiegelte Eingang ist auf diesem Gerät noch nicht eingerichtet. Das
            geht in den Einstellungen unter „Versiegelter Eingang“ — dort entsteht das
            Schlüsselpaar, mit dem der Einlieferer versiegelt.
          </p>
        )}

        {lauf && (
          <div className="doc-run">
            <p>
              Eingang wird geholt{lauf.gesamt > 0 ? ` — ${lauf.fertig} von ${lauf.gesamt}` : ' …'}
            </p>
            <div className="doc-bar">
              <span
                style={{ width: `${Math.round((lauf.fertig / Math.max(1, lauf.gesamt)) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {!lauf && bereit === true && (
          <p className="todos-hint">
            <button className="capture-undo" onClick={() => void abholen(false)}>
              Eingang jetzt holen
            </button>
            {luecken.length > 0 && (
              <>
                {' '}
                <button className="capture-undo" onClick={() => void nachholen(luecken)}>
                  {luecken.length} unvollständige nachholen
                </button>
              </>
            )}
          </p>
        )}

        {offen > 0 && (
          <p className="todos-hint">
            {offen === 1 ? 'Ein Auftrag liegt' : `${offen} Aufträge liegen`} beim Einlieferer. Was
            fertig ist, kommt beim nächsten „Eingang jetzt holen" an und ersetzt den alten Stand.
          </p>
        )}

        {bericht && <p className="todos-hint">{bericht}</p>}

        <ul className="doc-grid">{shown.map(kachel)}</ul>

        {shown.length === 0 && !lauf && (
          <p className="empty">{filter ? 'Nichts gefunden.' : 'Noch nichts gesammelt.'}</p>
        )}
      </div>
    </section>
  );
}
